import { useMemo } from 'react';
import {
    DEFAULT_SCIENCES,
    TEAM_PLANET_BUILDING_QUEUE_LIMIT,
    type BuildingType,
    type Planet,
} from '@kaeliux/shared';
import { extractScienceLevels } from '@kaeliux/game-engine';
import { zoneRouteFrom, type PlanetView } from '../contexts/TeamPlanetViewContext';
import { cancelTeamPlanetBuildingUpgrade, upgradeTeamPlanetBuilding } from '../services/teamPlanet.service';
import { TEAM_PLANET_BUILDING_RACE } from '../utils/teamPlanetStyle';
import { useStoreTeamPlanet } from './useTeamPlanet';

export const TEAM_PLANET_HOME_ROUTE = '/team-planet';

/** The Team planet has no Recycling or Market screens of its own — only research, the shared shipyards and the Tech Center. */
const TEAM_PLANET_SCREEN_ROUTE: Partial<Record<BuildingType, string>> = {
    laboratory: `${TEAM_PLANET_HOME_ROUTE}/sciences`,
    shipyard: `${TEAM_PLANET_HOME_ROUTE}/shipyard`,
    techCenter: `${TEAM_PLANET_HOME_ROUTE}/starport`,
};

export const TEAM_PLANET_READ_ONLY_REASON = 'Only the alliance leader and officers manage this';

/** The `PlanetView` every `/team-planet/*` screen runs under: Team planet mutations, no Rush, no destroy, all-race architecture. */
export function useTeamPlanetView(teamPlanet: Planet | null, canManage: boolean): PlanetView {
    const store = useStoreTeamPlanet();
    const sciences = teamPlanet?.sciences;

    return useMemo<PlanetView>(() => {
        return {
            kind: 'teamPlanet',
            ready: true,
            canManage,
            readOnlyReason: canManage ? null : TEAM_PLANET_READ_ONLY_REASON,
            allowRush: false,
            allowDestroy: false,
            scienceLevels: extractScienceLevels(sciences ?? DEFAULT_SCIENCES),
            buildingQueueLimit: TEAM_PLANET_BUILDING_QUEUE_LIMIT,
            queueMaxAccess: 'hidden',
            canUnlockMoreSlots: false,
            homeRoute: TEAM_PLANET_HOME_ROUTE,
            zoneRoute: zoneRouteFrom(TEAM_PLANET_HOME_ROUTE),
            screenRoute: (buildingType) => TEAM_PLANET_SCREEN_ROUTE[buildingType],
            buildingRace: (buildingType) => TEAM_PLANET_BUILDING_RACE[buildingType],
            upgradeBuilding: async (_planet, buildingType, _options, plotId) => {
                await store(await upgradeTeamPlanetBuilding(buildingType, plotId));
            },
            cancelBuildingUpgrade: async (_planet, eventId) => {
                await store(await cancelTeamPlanetBuildingUpgrade(eventId));
            },
        };
    }, [store, sciences, canManage]);
}
